import { CurrencyPipe, DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { AnnualStatementStatus, AnnualStep, SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { DocumentSubFolderName } from "@models/common/document-sub-folder-name.enum";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { ReminderType } from "@models/reminders/reminder.enum";
import { Trust } from "@models/trusts/trust";
import { DocumentToAttach, FilesResponse } from "@models/ui-models/files";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import { TagComponent } from "@shared/components/commons/tag/tag.component";
import {
  RemindersSidebarComponent
} from "@shared/components/documents-related/reminders-sidebar/reminders-sidebar.component";
import {
  AgAnnualStatusComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-status/ag-annual-status.component";
import {
  AgDocumentSigningStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-signing-status/ag-document-signing-status.component";
import {
  AgTagEntityTitleComponent
} from "@shared/components/grid/ag-cells/common/ag-tag-entity-title/ag-tag-entity-title.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { ReplaceStrPipe } from "@shared/pipes/replace-str.pipe";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { FilesService } from "@shared/services/files.service";
import { SignalRService } from "@shared/services/signalr.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap
} from "rxjs";
import {
  DocumentsListAccordionComponent
} from "../../../../../generate-documents/components/documents-list-accordion/documents-list-accordion.component";

@Component({
  selector: 'app-annual-statement-sidebar',
  standalone: true,
  imports: [
    NgxSkeletonLoaderModule,
    CurrencyPipe,
    DatePipe,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbTooltip,
    AgTagEntityTitleComponent,
    AgAnnualStatusComponent,
    TagComponent,
    ButtonComponent,
    DocumentsListAccordionComponent,
    CheckboxComponent,
    RemindersSidebarComponent,
    NgbDropdownItem,
    ReplaceStrPipe,
    AgDocumentSigningStatusComponent
  ],
  templateUrl: 'annual-statement-sidebar.component.html',
  styleUrl: 'annual-statement-sidebar.component.scss'
})
export class AnnualStatementSidebarComponent implements OnInit {
  companiesApiService = inject(CompaniesApiService);
  annualStatementsService = inject(AnnualStatementsService);
  filesService = inject(FilesService);
  filesApiService = inject(FilesApiService);
  documentCommonService = inject(DocumentCommonService);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);
  signalRService = inject(SignalRService);

  @Input() loadCompany = false;
  @Input() entityType = EntityType.Company;
  @Input() entity: Company | Trust | null = null;
  @Input() currentStepIndex: number | undefined;

  @Input() set annualStatement(value: AnnualStatement) {
    if (!value) return;
    this.annual = value;
    this.annual$.next(value);
  }

  @Output() updateAnnualStatement = new EventEmitter<boolean>();

  readonly documentsToAttachInfo = 'You can attach unlodged ASIC forms to the annual statement documents.' +
    ' To do so, select the forms you want to attach by ticking the appropriate boxes.';
  readonly filesToAttachInfo = 'You may upload up to 10 PDF files, including your own invoice, with a total size limit of 2 MB.';
  readonly ReminderType = ReminderType;
  readonly SigningStatus = SigningStatus;
  readonly DocumentSubFolderName = DocumentSubFolderName;
  readonly annual$ = new ReplaySubject<AnnualStatement>(1);
  readonly refreshList$ = new BehaviorSubject(false);
  readonly DocumentStatusEnum = DocumentStatusEnum;
  readonly AnnualStep = AnnualStep;
  readonly AnnualStatementStatus = AnnualStatementStatus;

  loading = false;
  annual: AnnualStatement | undefined;
  documentsList: FilesResponse | null = null;
  documentsToAttach: DocumentToAttach[] = [];

  ngOnInit(): void {
    this.loadRecentSignDocuments();
    this.listenSignalRMessages();
    this.listenDocumentsListChanges();
  }

  listenDocumentsListChanges(): void {
    combineLatest([
      this.refreshList$,
      this.annual$
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([_, annual]) => this.currentStepIndex === AnnualStep.SignDocuments),
        tap(() => {
          this.loading = true;
          this.cdr.detectChanges();
        }),
        filter(([_, annual]) => !!annual),
        debounceTime(2000), // temporary fix for no generated files issue
        switchMap(([_, annual]) => this.filesApiService.getFiles(annual.documentId)),
        tap(() => this.loading = false),
      )
      .subscribe({
        next: (documentsList) => {
          this.documentsList = documentsList;
        },
        error: (error) => {
          console.warn('[listenDocumentsListChanges] error: ', error);
        }
      });
  }

  listenSignalRMessages(): void {
    this.signalRService
      .onDocumentEnvelopeCompleted()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          if (message === this.annual?.documentId) {
            console.log('Envelope complete processing for message:', message);
            this.refreshList$.next(true);
          }
        },
        error: (err) => {
          console.error('Error handling SignalR message:', err);
        },
      });
  }

  get showPaymentReminder(): boolean {
    if (this.annual?.company.companyDebt?.amountOwing && this.annual?.company.companyDebt?.amountOwing > 0) {
      return true;
    }
    return false;
  }

  loadRecentSignDocuments(): void {
    this.annual$.pipe(
      filter(() => this.currentStepIndex === AnnualStep.SignDocuments),
      switchMap(annual => {
        return this.companiesApiService.getRecentDocuments(annual?.entityId ?? '', annual?.documentSigning?.signingStatus);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(documentsToAttach => {
      this.documentsToAttach = documentsToAttach.map(file => ({ ...file, selected: false }));
      this.annualStatementsService.documentsToAttach.set(this.documentsToAttach);
    });
  }

  downloadFile(fileName: string, subFolder: string): void {
    this.documentCommonService.downloadFile(this.annual!.documentId, fileName, subFolder);
  }

  deleteFile(fileName: string, subfolder = DocumentSubFolderName.Upload): void {
    this.documentCommonService.deleteFile(this.annual!.documentId, fileName, subfolder).pipe(
      tap((result: boolean) => {
        if (result)
          this.refreshList$.next(true);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  openUploadFileModal(subfolder: DocumentSubFolderName) {
    this.filesService.openUploadFilesModal(this.annual?.documentId!, subfolder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshList$.next(true));
  }

  downloadCombinedPDF(subFolder: string): void {
    this.documentCommonService;
    this.filesApiService
      .downloadFolder(this.annual!.documentId, true, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Annual Statement.pdf' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, `Annual Statement ${ this.annual?.company.name }.pdf`);
        }
      });
  }

  downloadZIP(subFolder: string): void {
    this.filesApiService
      .downloadFolder(this.annual!.documentId, false, subFolder)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download 'Annual Statement.zip' error", "Error");
          return of('ERR');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        if (res !== 'ERR') {
          downloadBase64File(res, "Annual Statement.zip");
        }
      });
  }

  selectAttachedDocument(selected: boolean, index: number): void {
    this.documentsToAttach[index].selected = selected;
    this.annualStatementsService.documentsToAttach.set(this.documentsToAttach);
  }

  get showRemindersSidebar(): boolean {
    return this.currentStepIndex === AnnualStep.SignDocuments
      && this.annual != undefined
      && this.annual.annualStatementStatus != AnnualStatementStatus.Archived
      && !AnnualStatement.isImportedDocument(this.annual);
  }
}
